import React, { ElementType, HTMLAttributes } from 'react';
import { cn } from '@/app/_lib/utils';

/**
 * Headline - Core Component
 *
 * Tokenized typography styles from design-tokens-oklch.css:
 * - H1: heading-xxl (Mobile: 30px/36px, Desktop: 72px/72px)
 * - H2: heading-md (30px/36px)
 * - H3: heading-sm (24px/32px)
 * - H4: text-xl fallback (20px/28px) - no token yet
 *
 * @see headline.token.css for CSS custom properties
 * @see https://www.figma.com/design/4fWGiNz1BdD4uMFX5Wfarg/SPARKS-Core-Kit-Foundations?node-id=473-1978
 */
const headlineStyles: Record<string, string> = {
  h1: 'font-h1-typeface lg:font-h1-typeface-lg text-h1 lg:text-h1-lg font-h1 lg:font-h1-lg tracking-h1 lg:tracking-h1-lg text-inherit mb-6',
  h2: 'font-h2-typeface lg:font-h2-typeface-lg text-h2 lg:text-h2-lg font-h2 lg:font-h2-lg tracking-h2 lg:tracking-h2-lg text-inherit mb-5',
  h3: 'font-h3-typeface lg:font-h3-typeface-lg text-h3 lg:text-h3-lg font-h3 lg:font-h3-lg tracking-h3 lg:tracking-h3-lg text-inherit mb-4',
  h4: 'font-h4-typeface lg:font-h4-typeface-lg text-h4 lg:text-h4-lg font-h4 lg:font-h4-lg tracking-h4 lg:tracking-h4-lg text-inherit mb-4',
};

export type HeadlineVariant = 'h1' | 'h2' | 'h3' | 'h4';

export interface HeadlineProps extends HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  className?: string;
  as?: ElementType;
  /**
   * Visual style variant, independent of semantic element.
   * If not provided, uses the style matching the `as` prop.
   */
  variant?: HeadlineVariant;
}

export function Headline({
  children,
  className,
  as: Tag = 'h1',
  variant,
  ...props
}: HeadlineProps) {
  const tagName = typeof Tag === 'string' ? Tag : 'h1';
  const styleKey = variant ?? tagName;
  const baseStyles = headlineStyles[styleKey] || headlineStyles.h1;

  return (
    <Tag
      className={cn(baseStyles, className)}
      {...props}
    >
      {children}
    </Tag>
  );
}
